import { mergeSx, useSessionStorage } from "@dmca/frontend/hooks";
import { Box, Typography, type BoxProps } from "@mui/material";
import { captureException } from "@sentry/react";
import { hash } from "ohash";
import { useEffect, useRef, useState } from "react";
import { isNetworkError } from "../is-network-error";

export interface DefaultErrorComponentProps extends BoxProps {
  error?: any;
}

export const DefaultErrorComponent: React.FC<DefaultErrorComponentProps> = ({
  sx,
  error,
  ...rest
}) => {
  const [reloadTimer, setReloadTimer] = useSessionStorage(
    "error-reload-timer",
    5
  );
  const [errorHash, setErrorHash] = useSessionStorage(
    "uncaught-error-hash",
    ""
  );
  const [timer, setTimer] = useState(reloadTimer);
  const cancelTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  // Click tracking state and refs
  const [clickCount, setClickCount] = useState(0);
  const clickTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  // Handler for clicks anywhere on the container
  const handleContainerClick = () => {
    setClickCount((prev) => prev + 1);

    // Reset click count after 2 seconds of inactivity
    clearTimeout(clickTimeoutRef.current);
    clickTimeoutRef.current = setTimeout(() => {
      setClickCount(0);
    }, 2000);
  };

  useEffect(() => {
    // Check if we've reached 5 clicks
    if (clickCount >= 5) {
      clearInterval(cancelTimeoutRef.current);
      setClickCount(0);
      alert("Auto-reload canceled.");
    }
  }, [clickCount]);

  useEffect(() => {
    const newHash = hash(error);
    if (error && newHash !== errorHash) {
      setErrorHash(newHash);
      captureException(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    console.error("Error:", error);
    cancelTimeoutRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          setReloadTimer((prev2) => (prev2 * 2 > 60 ? 60 : prev2 * 2));
          window.location.reload();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Clean up both intervals on unmount
    return () => {
      clearInterval(cancelTimeoutRef.current);
      clearTimeout(clickTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      {...rest}
      onClick={handleContainerClick}
      sx={mergeSx(
        {
          width: "100%",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          "& > .MuiTypography-root": {
            textAlign: "center",
          },
        },
        sx
      )}
    >
      <Typography>
        {isNetworkError(error)
          ? `Can't reach the server. Check the network connection — the app will retry in ${timer} seconds.`
          : `An unknown error occured. Please contact support if it persists. The app will reload in ${timer} seconds.`}
      </Typography>
      <Typography>
        <Typography
          component="span"
          sx={{
            color: "primary.main",
            cursor: "pointer",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
          onClick={(e) => {
            e.stopPropagation();
            window.location.reload();
          }}
        >
          Click here
        </Typography>{" "}
        to reload now.
      </Typography>
    </Box>
  );
};
